import {forwardRef} from "react";
import {
  XCol,
  XRow,
  XText,
  ArrowOverlay,
  rules,
  XForm,
  RadioGroupWithLabel,
  XTextButton,
  XPush,
  uiStyles,
  XHorLine,
  DropDownForChild,
} from "@cdx/common";
import useStateIfLoaded from "../../lib/hooks/useStateIfLoaded";
import useMutation from "../../lib/hooks/useMutation";
import {useLogout} from "../../lib/auth";
import CreateAccountOverlay, {JoinCommunityOverlayLayout} from "./CreateAccountOverlay";
import {joinAnd} from "../../lib/utils";
import {Col, DSButton} from "@cdx/ds";

const signupFormRules = {
  frequency: [],
  email: [rules.isEmail, rules.isRequired],
};

const frequencyOptions = [
  {value: null, label: "No Digest"},
  {value: 1, label: "Daily updates"},
  {value: 7, label: "Weekly updates"},
];

const ManageMembership = ({digestInfo, meId, project, onClose}) => {
  const {state, setState, isLoaded} = useStateIfLoaded(
    () => ({
      frequency: digestInfo.digestFrequencyInDays,
    }),
    [digestInfo.createdAt]
  );
  const [doUpdate] = useMutation("publicProjectMembership", "update");
  const [doDelete] = useMutation("publicProjectMembership", "delete");
  const projectId = project.$meta.get("id");

  const doLogout = useLogout();

  const handleSubmit = () => {
    if (!isLoaded) return;
    return doUpdate({userId: meId, projectId, digestFrequencyInDays: state.frequency});
  };
  const handleLeave = () => doDelete({userId: meId, projectId});

  return (
    <XCol>
      <XCol pa={3} sp={2} bg="white">
        <XCol sp={1}>
          <XText preset="bold" size={3}>
            You're part of this Community
          </XText>
          {project.allowUpvotes && (
            <XText size={2} color="gray600">
              You can upvote cards and will get updates.
            </XText>
          )}
        </XCol>
        <XHorLine color="gray300" />
        <XText preset="bold" size={2}>
          Update your Notification preferences
        </XText>
        <XCol border="purple200" px={3} py={2} rounded="sm">
          <XCol
            as={XForm}
            rules={signupFormRules}
            values={state}
            onChange={setState}
            sp={3}
            onSubmit={handleSubmit}
            buttonLabel="Save"
          >
            <XForm.Field
              as={RadioGroupWithLabel}
              options={frequencyOptions}
              hint="You'll only get a message if something has actually changed"
              name="frequency"
              label={null}
            />
          </XCol>
        </XCol>
      </XCol>
      <XRow px={3} py={1} sp={2}>
        <XTextButton onClick={handleLeave} color="redOnHover">
          Leave Community
        </XTextButton>
        <XPush />
        <XTextButton to="?show=userProfile">Profile</XTextButton>
        <XTextButton onClick={() => doLogout().then(onClose)}>Logout</XTextButton>
      </XRow>
    </XCol>
  );
};

const Button = forwardRef(({onClick, active, isActive}, ref) => (
  <DSButton
    variant={isActive ? "secondary" : "primary"}
    size="sm"
    onClick={onClick}
    ref={ref}
    active={active}
    negatePadding
  >
    {isActive ? (
      <>
        <span>Manage</span>
        <span className={uiStyles.hide.sm}> membership</span>
      </>
    ) : (
      <>
        <span>Join</span>
        <span className={uiStyles.hide.sm}> community</span>
      </>
    )}
  </DSButton>
));

export const JoinAsLoggedInUser = ({title, explainer, projectId, meId, onDone}) => {
  const [doCreate] = useMutation("publicProjectMembership", "create");
  const handleJoin = () =>
    doCreate({userId: meId, projectId, digestFrequencyInDays: 7}).then(() => {
      if (onDone) onDone();
    });

  return (
    <JoinCommunityOverlayLayout title={title} explainer={explainer}>
      <Col px="16px" py="12px">
        <DSButton size="lg" onClick={handleJoin}>
          Join
        </DSButton>
      </Col>
    </JoinCommunityOverlayLayout>
  );
};

const CommunityButton = ({root, project}) => {
  const projectId = project.$meta.get("id");
  const meId = root.loggedInUser && root.loggedInUser.$meta.get("id");
  const digestInfo =
    projectId &&
    root.loggedInUser &&
    root.loggedInUser.$meta.first("publicProjectMembership", {projectId, $order: "createdAt"});

  const explaining = [
    meId ? "Stay up to date on the latest changes" : "Register here to keep track of updates",
  ];
  if (project.allowUpvotes) explaining.push(meId ? "start upvoting cards" : "upvote cards");
  return (
    <DropDownForChild
      placement="bottom"
      distanceFromAnchor={10}
      renderOverlay={({close, ...props}) => (
        <ArrowOverlay bg={digestInfo ? "gray200" : "purple700"} {...props}>
          {digestInfo ? (
            <ManageMembership
              digestInfo={digestInfo}
              meId={meId}
              project={project}
              onClose={close}
            />
          ) : meId ? (
            <JoinAsLoggedInUser explainer={joinAnd(explaining)} meId={meId} projectId={projectId} />
          ) : (
            <CreateAccountOverlay explainer={joinAnd(explaining)} />
          )}
        </ArrowOverlay>
      )}
      setChildProps={({isOpen}) => ({active: isOpen})}
    >
      <Button isActive={digestInfo && digestInfo.$meta.isLoaded} />
    </DropDownForChild>
  );
};

export default CommunityButton;
